import { ReactNode } from 'react'
import { TableSummaryItem } from '../../../../../../generic/grid-table'
import type { IReceiptReportParams } from '../../../../../../modules/API/Reciept/models/requests'
import BankIcon from '../../../../../../generic/icons/IconLib/BankIcon'
import CalendarCheckIcon from '../../../../../../generic/icons/IconLib/CalendarCheckIcon'
import CalendarIcon from '../../../../../../generic/icons/IconLib/CalendarIcon'
import ChequeIcon from '../../../../../../generic/icons/IconLib/ChequeIcon'
import CoinIcon from '../../../../../../generic/icons/IconLib/CoinIcon'
import CreditCardIcon from '../../../../../../generic/icons/IconLib/CreditCardIcon'
import HashIcon from '../../../../../../generic/icons/IconLib/HashIcon'
import ShekelIcon from '../../../../../../generic/icons/IconLib/ShekelIcon'
import XCircleIcon from '../../../../../../generic/icons/IconLib/XCircleIcon'
import { formatFixed } from '../../../procedures/shared/helpers/formatNumber'
import { convertToDate } from '../../../../../../modules/convertDateString'
import { PAYMENT_TYPE, PaymentType } from '../../../receipt/generic/getDetailesByPaymentType'
import type { IReceiptsReportStats } from '../types/stats'
import SummaryValue from '../Components/SummaryValue'

const ICON_SIZE = 14

const formatLinesCount = (count: number): string => (count === 1 ? 'שורה אחת' : `${count} שורות`)

/** The start on the first line, the end (when it differs) on the second — a one-line range does not fit a cell. */
const formatDateRange = (filters: IReceiptReportParams | null): ReactNode => {
  if (!filters) return '—'
  const to = filters.DateTo !== filters.DateFrom ? `עד ${convertToDate(filters.DateTo)}` : undefined
  return <SummaryValue main={convertToDate(filters.DateFrom)} sub={to} />
}

// PAYMENT_TYPE icons are built at size 20 — the summary needs its own small ones.
const PAYMENT_TYPE_ICONS: Record<PaymentType, ReactNode> = {
  'מ': <CoinIcon size={ICON_SIZE} />,
  'ה': <BankIcon size={ICON_SIZE} />,
  'ש': <ChequeIcon size={ICON_SIZE} />,
  'כ': <CreditCardIcon size={ICON_SIZE} />,
}

export const buildReceiptsReportSummaryItems = (
  stats: IReceiptsReportStats,
  appliedFilters: IReceiptReportParams | null,
): TableSummaryItem[] => [
  {
    key: 'dateRange',
    label: 'תאריכים',
    value: formatDateRange(appliedFilters),
    icon: <CalendarIcon size={ICON_SIZE} />,
  },
  { key: 'totalSum', label: 'סה״כ', value: formatFixed(stats.totalSum), icon: <ShekelIcon size={ICON_SIZE} /> },
  { key: 'receiptsCount', label: 'מס׳ קבלות', value: stats.receiptsCount, icon: <HashIcon size={ICON_SIZE} /> },
  {
    key: 'averageReceipt',
    label: 'קבלה ממוצעת',
    value: formatFixed(stats.averageReceipt),
    icon: <ShekelIcon size={ICON_SIZE} />,
  },
  ...(Object.keys(PAYMENT_TYPE) as PaymentType[]).map((type) => ({
    key: `paymentType_${type}`,
    label: PAYMENT_TYPE[type].name,
    value: (
      <SummaryValue
        main={formatFixed(stats.byPaymentType[type].sum)}
        sub={`${stats.byPaymentType[type].percent.toFixed(1)}%`}
      />
    ),
    icon: PAYMENT_TYPE_ICONS[type],
  })),
  {
    key: 'pendingCheques',
    label: 'שיקים לפירעון',
    value: formatFixed(stats.pendingChequesSum),
    icon: <CalendarCheckIcon size={ICON_SIZE} />,
  },
  {
    key: 'canceled',
    label: 'מבוטלים',
    value: <SummaryValue main={formatFixed(stats.canceledSum)} sub={formatLinesCount(stats.canceledCount)} />,
    icon: <XCircleIcon size={ICON_SIZE} />,
  },
]
